import { useEffect, useRef, useState } from "react";

/** How often to look at a frame. Decoding is the expensive part. */
const FRAME_INTERVAL_MS = 300;

type Problem =
  | "not_loaded"
  | "insecure"
  | "denied"
  | "no_camera"
  | "unavailable"
  | "decode_failed";

const MESSAGES: Record<Problem, string> = {
  not_loaded:
    "The camera reader could not be loaded. Check the connection, or close and reopen the app — it may have just been updated.",
  insecure: "The camera needs a secure connection (HTTPS).",
  denied: "Camera access was refused. Allow it in the browser.",
  no_camera: "This device has no usable camera.",
  unavailable: "The camera could not be started.",
  decode_failed: "Could not read anything. Type the label instead.",
};

/**
 * The fallback for a tablet with no scanner attached.
 *
 * A camera is slower and fussier than a handheld scanner, which is why
 * it is behind a button rather than the default. It reads only Code128
 * and only accepts something shaped like a cart tag, so a comic held in
 * frame cannot start a timer.
 */
export function CameraScanner({
  onScan,
  onClose,
  onFailed,
}: {
  onScan: (code: string) => void;
  onClose: () => void;
  /**
   * Told once, when the camera cannot read anything. The screen ignores
   * the handheld scanner while the camera is open so two readers cannot
   * fire on one tag — and a camera that failed is not reading.
   */
  onFailed?: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [ready, setReady] = useState(false);
  const failedRef = useRef(onFailed);
  failedRef.current = onFailed;

  useEffect(() => {
    // Guards against a late frame firing onScan twice, and against the
    // async setup finishing after the component has gone.
    let cancelled = false;
    let stream: MediaStream | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;
    const canvas = document.createElement("canvas");
    // One decode at a time. tryHarder on a slow tablet can take longer
    // than the interval, and overlapping calls only queue up behind it.
    let decoding = false;

    const fail = (why: Problem) => {
      if (cancelled) return;
      // A problem hides the video, so the camera goes off with it — a
      // light left on behind an error message is the one outcome worse
      // than the error.
      stop();
      setProblem(why);
      failedRef.current?.();
    };

    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
      // The single most important line here. A track left running keeps
      // the camera light on, which on a shared tablet looks exactly like
      // the shop filming its staff.
      for (const track of stream?.getTracks() ?? []) track.stop();
      stream = null;
    };

    const tick = async () => {
      const video = videoRef.current;
      if (cancelled || decoding || !video || video.readyState < 2) return;

      const width = video.videoWidth;
      const height = video.videoHeight;
      if (!width || !height) return;

      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) return;

      context.drawImage(video, 0, 0, width, height);
      decoding = true;
      try {
        const { decodeJobLabel } = await import("../lib/barcode.js");
        const label = await decodeJobLabel(
          context.getImageData(0, 0, width, height),
        );
        if (label && !cancelled) {
          cancelled = true;
          stop();
          onScan(label);
        }
      } catch {
        // A frame with no barcode resolves empty; a throw is the decoder
        // itself failing. This used to test `ready`, captured false from
        // the first render, so one throw hid the video and left the
        // stream and the interval running behind the message.
        fail("decode_failed");
      } finally {
        decoding = false;
      }
    };

    const start = async () => {
      // The decoder and its megabyte of wasm load here, on first use of
      // the camera, rather than in the bundle every tablet fetches at
      // the start of a shift. Most of them have a scanner and will never
      // open this.
      // The wasm URL is fetched here rather than imported at module
      // scope, alongside the decoder it configures. `?url` is a Vite
      // import: resolving it eagerly put a bundler-specific specifier in
      // WorkScreen's module graph, so importing that screen outside Vite
      // — in a component test — failed on a wasm file Node cannot
      // resolve. Vite still emits the asset either way.
      //
      // And it can fail: the server answers 404 for a hashed asset that
      // is no longer there, so a tablet still on the previous build after
      // a deploy cannot fetch it. Unhandled, that left "Starting the
      // camera…" on screen for ever.
      try {
        const [{ configureBarcodeReader }, { default: wasmUrl }] = await Promise.all([
          import("../lib/barcode.js"),
          import("zxing-wasm/reader/zxing_reader.wasm?url"),
        ]);
        configureBarcodeReader(wasmUrl);
      } catch {
        fail("not_loaded");
        return;
      }

      // getUserMedia is undefined outside a secure context, which is a
      // different problem from the employee saying no.
      if (!navigator.mediaDevices?.getUserMedia) {
        fail(window.isSecureContext ? "no_camera" : "insecure");
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          // The back camera: nobody scans a cart tag with the selfie one.
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (cancelled) {
          for (const track of stream.getTracks()) track.stop();
          return;
        }
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play();
        // Unmounted while play() was pending: cleanup has already run,
        // so an interval started now would never be cleared.
        if (cancelled) {
          stop();
          return;
        }
        setReady(true);
        timer = setInterval(() => void tick(), FRAME_INTERVAL_MS);
      } catch (err) {
        const name = (err as { name?: string })?.name;
        fail(
          name === "NotAllowedError" || name === "SecurityError"
            ? "denied"
            : name === "NotFoundError"
              ? "no_camera"
              : "unavailable",
        );
      }
    };

    void start();
    return () => {
      cancelled = true;
      stop();
    };
    // Mount once. onScan is called at most once and then this unmounts.
  }, []);

  return (
    <section className="card camera">
      <div className="row">
        <h2>Camera</h2>
        <button type="button" className="ghost" onClick={onClose}>
          Cancel
        </button>
      </div>

      {problem ? (
        <p className="error" role="alert">
          {MESSAGES[problem]}
        </p>
      ) : (
        <p className="muted">
          {ready ? "Hold the label up to the camera." : "Starting the camera…"}
        </p>
      )}

      <video
        ref={videoRef}
        className="camera-view"
        playsInline
        muted
        hidden={Boolean(problem)}
      />
    </section>
  );
}
